<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import { Head } from '@inertiajs/vue3';
import { Coins, FileText, Globe, Shield, TrendingUp } from 'lucide-vue-next';
</script>

<template>
    <Head title="Whitepaper">
        <meta
            head-key="description"
            name="description"
            content="DigitalSafe is an innovative platform for storing and verifying valuable documents and physical assets in digital form. In an increasingly digital world, where identity theft, privacy breaches, and document fraud are growing threats, DigitalSafe offers a secure foundation for modern information management."
        />
        <meta
            name="keywords"
            content="Digital Safe, Introduction, Digital Safe Introduction, Introduction, Purpose of the $DS Token, Token Issuance Mechanics, Jurisdiction & Regulatory Compliance, Risk Factors"
        />
    </Head>
    <div class="min-h-screen bg-background">
        <Header />
        <main class="pt-20">
            <section class="relative overflow-hidden py-20">
                <div class="bg-gradient-subtle absolute inset-0 opacity-50"></div>
                <div class="relative z-10 container mx-auto max-w-4xl px-4 text-center">
                    <h1 class="mb-6 text-4xl font-bold md:text-6xl">
                        <span class="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">DigitalSafe</span> Whitepaper
                    </h1>
                    <p class="mb-8 text-lg leading-relaxed text-muted-foreground md:text-xl">
                        DigitalSafe ($DS) Utility Token – Draft Compliant with MiCA
                    </p>
                    <div class="glass-card inline-block px-6 py-3">
                        <span class="font-semibold text-primary">$DS is not a security - Utility Token Only</span>
                    </div>
                </div>
            </section>

            <section class="py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-8 flex items-center space-x-4">
                        <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <FileText class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h2 class="text-3xl font-bold md:text-4xl">Introduction</h2>
                    </div>
                    <div class="glass-card mb-8 p-8">
                        <p class="mb-6 text-lg leading-relaxed">
                            DigitalSafe is an innovative platform for storing and verifying valuable documents and physical assets in digital form. In
                            an increasingly digital world, where identity theft, privacy breaches, and document fraud are growing threats, DigitalSafe
                            offers a secure foundation for modern information management.
                        </p>
                        <p class="mb-6 text-lg leading-relaxed">
                            The platform combines advanced encryption, certification, and decentralized storage technologies, giving users full
                            control over their digital assets. DigitalSafe aims to create a space where personal data, contracts, property deeds,
                            medical records, and digital resources are protected, authorized, and verifiable in a transparent and reliable manner.
                        </p>
                        <p class="text-lg leading-relaxed">
                            The $DS token is designed as a utility tool that supports platform functionalities and strengthens the DigitalSafe
                            ecosystem through access, reward, and validation mechanisms.
                        </p>
                    </div>
                    <div class="rounded-lg border border-accent/20 bg-accent/10 p-6 text-center">
                        <p class="font-semibold text-accent">
                            Note: The $DS token is not a security. It does not represent profit sharing nor is it a financial instrument.
                        </p>
                    </div>
                </div>
            </section>

            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-8 flex items-center space-x-4">
                        <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <Coins class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h2 class="text-3xl font-bold md:text-4xl">Purpose of the $DS Token</h2>
                    </div>

                    <div class="glass-card mb-8 p-8">
                        <p class="mb-8 text-lg leading-relaxed">
                            The $DS token serves solely as a utility token within the DigitalSafe platform and is an integral part of the ecosystem.
                            It is designed to provide access to advanced features and create incentives that promote platform growth.
                        </p>

                        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <div class="space-y-4">
                                <div class="flex items-start space-x-3">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <p>Increase user engagement through loyalty programs and reward mechanisms</p>
                                </div>
                                <div class="flex items-start space-x-3">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <p>Enable access to premium services such as extended storage, version history access, and priority encryption</p>
                                </div>
                                <div class="flex items-start space-x-3">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <p>Serve as a payment method for document certification and Proof-of-Authenticity services</p>
                                </div>
                            </div>
                            <div class="space-y-4">
                                <div class="flex items-start space-x-3">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <p>Support a reputation system based on trust and decentralization</p>
                                </div>
                                <div class="flex items-start space-x-3">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <p>Provide discounts on transaction fees and access to exclusive community features</p>
                                </div>
                                <div class="flex items-start space-x-3">
                                    <div class="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></div>
                                    <p>Act as a voting mechanism for future governance features (without ownership rights)</p>
                                </div>
                            </div>
                        </div>

                        <div class="mt-8 rounded-lg bg-muted/20 p-4 text-sm text-muted-foreground">
                            The token does not grant dividend rights, is not an investment vehicle, and does not represent participation in any fund.
                            Its value derives exclusively from its use within the platform.
                        </div>
                    </div>
                </div>
            </section>

            <section class="py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-8 flex items-center space-x-4">
                        <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <TrendingUp class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h2 class="text-3xl font-bold md:text-4xl">Token Issuance Mechanics</h2>
                    </div>

                    <div class="mb-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
                        <div class="glass-card p-6">
                            <h3 class="mb-4 text-xl font-semibold">Token Supply</h3>
                            <div class="space-y-3">
                                <div class="flex justify-between">
                                    <span>Total Supply:</span>
                                    <span class="font-bold text-primary">1,000,000,000 $DS</span>
                                </div>
                                <div class="flex justify-between">
                                    <span>Issuance Model:</span>
                                    <span class="font-bold">Fixed Supply</span>
                                </div>
                                <div class="flex justify-between">
                                    <span>Future Minting:</span>
                                    <span class="font-bold text-accent">Not Possible</span>
                                </div>
                            </div>
                        </div>

                        <div class="glass-card p-6">
                            <h3 class="mb-4 text-xl font-semibold">Key Features</h3>
                            <div class="space-y-3">
                                <div class="flex items-center space-x-2">
                                    <Shield class="h-4 w-4 text-primary" />
                                    <span>Deflationary mechanism</span>
                                </div>
                                <div class="flex items-center space-x-2">
                                    <Shield class="h-4 w-4 text-primary" />
                                    <span>Multisig treasury control</span>
                                </div>
                                <div class="flex items-center space-x-2">
                                    <Shield class="h-4 w-4 text-primary" />
                                    <span>Open-source & audited</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="glass-card p-8">
                        <h3 class="mb-6 text-center text-2xl font-semibold">Distribution Structure</h3>
                        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            <div class="text-center">
                                <div
                                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60"
                                >
                                    <span class="text-2xl font-bold text-primary-foreground">50%</span>
                                </div>
                                <h4 class="mb-2 font-semibold">Public Sale</h4>
                                <p class="text-sm text-muted-foreground">Infrastructure, marketing, expansion</p>
                            </div>
                            <div class="text-center">
                                <div
                                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-accent to-accent/60"
                                >
                                    <span class="text-2xl font-bold text-accent-foreground">20%</span>
                                </div>
                                <h4 class="mb-2 font-semibold">Development Fund</h4>
                                <p class="text-sm text-muted-foreground">24-month release schedule</p>
                            </div>
                            <div class="text-center">
                                <div
                                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-secondary/60"
                                >
                                    <span class="text-2xl font-bold text-secondary-foreground">15%</span>
                                </div>
                                <h4 class="mb-2 font-semibold">Team</h4>
                                <p class="text-sm text-muted-foreground">6-month cliff, 18-month vesting</p>
                            </div>
                            <div class="text-center">
                                <div
                                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-muted to-muted/60"
                                >
                                    <span class="text-2xl font-bold text-muted-foreground">10%</span>
                                </div>
                                <h4 class="mb-2 font-semibold">Partners</h4>
                                <p class="text-sm text-muted-foreground">Strategic alliances</p>
                            </div>
                            <div class="text-center md:col-span-2 lg:col-span-1">
                                <div
                                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary/40 to-accent/40"
                                >
                                    <span class="text-2xl font-bold text-foreground">5%</span>
                                </div>
                                <h4 class="mb-2 font-semibold">Community</h4>
                                <p class="text-sm text-muted-foreground">Airdrops & incentives</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-8 flex items-center space-x-4">
                        <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <Globe class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h2 class="text-3xl font-bold md:text-4xl">Jurisdiction & Regulatory Compliance</h2>
                    </div>
                    <div class="glass-card p-8">
                        <p class="mb-8 text-lg leading-relaxed">
                            DigitalSafe plans to issue the $DS token in compliance with regulations in selected jurisdictions while minimizing
                            regulatory burdens. As an open-source, decentralized project (DEX-only), its strategy includes:
                        </p>
                        <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                            <div class="space-y-6">
                                <div>
                                    <h4 class="mb-2 font-semibold text-primary">Utility Token</h4>
                                    <p class="text-sm text-muted-foreground">
                                        $DS is not a security, does not grant profit rights or ownership in the company; it functions solely within
                                        the platform.
                                    </p>
                                </div>
                                <div>
                                    <h4 class="mb-2 font-semibold text-primary">Geographic Restrictions</h4>
                                    <p class="text-sm text-muted-foreground">
                                        Token sales will be geographically restricted via geo-fencing and legal disclaimers.
                                    </p>
                                </div>
                                <div>
                                    <h4 class="mb-2 font-semibold text-primary">DEX-Only Model</h4>
                                    <p class="text-sm text-muted-foreground">
                                        Token will not initially be listed on centralized exchanges, reducing MiCA obligations.
                                    </p>
                                </div>
                            </div>
                            <div class="space-y-6">
                                <div>
                                    <h4 class="mb-2 font-semibold text-primary">No Initial KYC/AML</h4>
                                    <p class="text-sm text-muted-foreground">
                                        Not required initially since $DS is purchased via smart contract on DEX.
                                    </p>
                                </div>
                                <div>
                                    <h4 class="mb-2 font-semibold text-primary">Jurisdiction Neutrality</h4>
                                    <p class="text-sm text-muted-foreground">No formal legal entity; open-source code with future DAO governance.</p>
                                </div>
                                <div>
                                    <h4 class="mb-2 font-semibold text-primary">Continuous Monitoring</h4>
                                    <p class="text-sm text-muted-foreground">Adapting to MiCA, SEC, and other global regulatory frameworks.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <h2 class="mb-8 text-center text-3xl font-bold md:text-4xl">Risk Factors</h2>
                    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div class="glass-card p-6">
                            <h3 class="mb-4 text-xl font-semibold text-accent">Market Risks</h3>
                            <ul class="space-y-2 text-sm">
                                <li>• High volatility due to speculation and market sentiment</li>
                                <li>• Low liquidity may hinder favorable trading conditions</li>
                            </ul>
                        </div>
                        <div class="glass-card p-6">
                            <h3 class="mb-4 text-xl font-semibold text-accent">Technological Risks</h3>
                            <ul class="space-y-2 text-sm">
                                <li>• Smart contract bugs despite audits</li>
                                <li>• MVP failure due to technical challenges</li>
                                <li>• Cybersecurity threats and exploits</li>
                            </ul>
                        </div>
                        <div class="glass-card p-6">
                            <h3 class="mb-4 text-xl font-semibold text-accent">Regulatory Risks</h3>
                            <ul class="space-y-2 text-sm">
                                <li>• Changing laws may impact token legality</li>
                                <li>• Regulatory restrictions despite MiCA compliance</li>
                            </ul>
                        </div>
                        <div class="glass-card p-6">
                            <h3 class="mb-4 text-xl font-semibold text-accent">Operational Risks</h3>
                            <ul class="space-y-2 text-sm">
                                <li>• User adoption failure impacting utility</li>
                                <li>• Team dependency on core members</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto max-w-4xl px-4 text-center">
                    <h2 class="mb-8 text-3xl font-bold md:text-4xl">Building a <span class="text-primary">Safer Digital Future</span></h2>
                    <p class="mb-8 text-lg leading-relaxed text-muted-foreground">
                        DigitalSafe represents a forward-looking initiative that merges security, decentralization, and utility to redefine how
                        physical assets and sensitive data are stored and managed in the digital age. By leveraging the power of blockchain, the $DS
                        token, and community-driven governance, the project provides a reliable infrastructure for the future of digital trust.
                    </p>
                    <div class="glass-card p-8">
                        <p class="text-lg leading-relaxed">
                            We invite all innovators, early adopters, and advocates of digital freedom to join us in building a safer, smarter
                            ecosystem—where what matters most is not only protected, but also empowered by the technology of tomorrow.
                        </p>
                    </div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
</template>
